import { useEffect, useState } from "react";
import BonLivraisonEmballages from "@/components/BonLivraisonEmballages";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/firebase/api-client";
import {
  Store as StoreIcon,
  Package,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Calendar,
} from "lucide-react";
import Loading from "@/components/shared/Loading";

interface StoreWithProductCount {
  id: string;
  name: string;
  glovoStoreId?: string;
  address?: string;
  isActive: boolean;
  productCount: number;
  /** Produits en attente de suppression. Null tant qu'on n'a pas lu. */
  enQuarantaine: number | null;
}

/**
 * Les emballages suivis en stock.
 *
 * « origine » n'y figure pas, et ce n'est pas un oubli : c'est l'emballage
 * du fournisseur, pas le tien. On ne lui remet rien, on ne lui en decompte
 * rien. L'inscrire ici ferait apparaitre partout une ligne a zero qui ne
 * bougerait jamais.
 */
const TYPES_EMBALLAGE = [
  "sac craft legume 2 kg",
  "craft epice 250 g",
  "craft epice 500 g",
  "boite plastique 250 g",
  "boite plastique 500 g",
  "boite plastique 1 kg",
];

export default function ProductsPage() {
  const [stores, setStores] = useState<StoreWithProductCount[]>([]);
  const [loading, setLoading] = useState(true);
  // Le magasin dont on s'apprete a vider la quarantaine, et le compte.

  // ----------------------------------------------------------- emballages
  const [emb, setEmb] = useState<any | null>(null);
  // 01/11/2026 : l'etat des emballages se charge sur demande, plus a
  // l'ouverture de la page. Ce calcul relit des milliers de commandes ; on
  // ouvre la page Produits pour le catalogue bien plus souvent que pour les
  // sacs.
  const [embChargement, setEmbChargement] = useState(false);
  const [embErreur, setEmbErreur] = useState<string | null>(null);
  // Le filtre magasin de l'affichage. "" = tous les magasins.
  // Il ne touche QUE ce qu'on regarde : la saisie d'une remise garde son
  // propre choix de magasin, sinon on remettrait des sacs au mauvais endroit
  // parce qu'un filtre d'affichage etait reste actif.
  const [filtreMagasin, setFiltreMagasin] = useState("");
  const [remiseOuverte, setRemiseOuverte] = useState(false);
  // Le magasin d'abord, le fournisseur ensuite : la liste des fournisseurs
  // est longue et melangee, alors que chacun ne travaille que pour un
  // magasin. Choisir le magasin la reduit a ce qui est possible.
  const [remiseMagasin, setRemiseMagasin] = useState("");
  const [remiseFrs, setRemiseFrs] = useState("");
  const [remiseType, setRemiseType] = useState(TYPES_EMBALLAGE[0]);
  const [remiseQte, setRemiseQte] = useState("");
  // Le prix d'achat d'UNE piece, en dirhams, tel qu'il est ecrit sur la
  // facture. La conversion en centimes se fait au serveur, une seule fois.
  const [remisePrix, setRemisePrix] = useState("");
  const [remiseEnCours, setRemiseEnCours] = useState(false);
  /** La remise qu'on s'apprete a annuler. */
  const [remiseASupprimer, setRemiseASupprimer] = useState<any | null>(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [editEmballage, setEditEmballage] = useState(TYPES_EMBALLAGE[0]);
  const [editQte, setEditQte] = useState("");
  const [editPrix, setEditPrix] = useState("");
  // TOUS LES BONS DE LIVRAISON D'EMBALLAGES SAISIS (04/10/2026).
  // Ils etaient enregistres, mais seul l'ecran du support les montrait.
  const [bons, setBons] = useState<any[]>([]);
  async function chargerBons() {
    try {
      const r = await api.emballages.bons();
      setBons(r.bons || []);
    } catch {
      // Un confort : son absence ne doit pas vider l'ecran.
      setBons([]);
    }
  }
  useEffect(() => {
    void chargerBons();
  }, []);



  const navigate = useNavigate();

  async function chargerEmballages(frais = false) {
    setEmbChargement(true);
    setEmbErreur(null);
    try {
      setEmb(await api.emballages.etat(undefined, undefined, frais));
    } catch (e: any) {
      setEmbErreur(e?.message || "Chargement impossible");
    } finally {
      setEmbChargement(false);
    }
  }


  async function enregistrerRemise(e: React.FormEvent) {
    e.preventDefault();
    const q = parseInt(remiseQte, 10);
    if (!remiseFrs || !Number.isFinite(q) || q <= 0) return;
    setRemiseEnCours(true);
    try {
      const prix = parseFloat(remisePrix.replace(",", "."));
      await api.emballages.remise({
        fournisseurId: remiseFrs,
        emballage: remiseType,
        quantite: q,
        // Le prix reste facultatif : on connait parfois la quantite avant la
        // facture, et bloquer la saisie ferait perdre l'information des sacs.
        ...(Number.isFinite(prix) && prix >= 0 ? { prixUnitaire: prix } : {}),
      });
      setRemiseOuverte(false);
      setRemiseQte("");
      setRemisePrix("");
      setRemiseFrs("");
      await chargerEmballages(true);
    } catch (err: any) {
      setEmbErreur(err?.message || "Enregistrement impossible");
    } finally {
      setRemiseEnCours(false);
    }
  }

  useEffect(() => {
    loadStores();
  }, []);

  async function loadStores() {
    try {
      setLoading(true);

      // Récupérer les stores via l'API (passe par le backend, pas de problème de permissions)
      const storesData = await api.stores.getAll();

      // Filtrer les stores inactifs et mapper les données
      const activeStores = (storesData || [])
        .filter((s: any) => s.isActive !== false)
        .map((s: any) => ({
          id: s.id,
          name: s.name || "Store sans nom",
          glovoStoreId: s.glovoStoreId,
          address: s.address,
          isActive: s.isActive ?? true,
          productCount: s.productCount || 0, // Utiliser le count du backend
          enQuarantaine: null,
        }));

      setStores(activeStores);

      // La quarantaine se lit APRES, magasin par magasin : c'est une lecture
      // secondaire, et la faire attendre l'affichage des cartes rendrait la
      // page lente pour une information qui n'est presque jamais urgente.
      void Promise.all(
        activeStores.map(async (st: StoreWithProductCount) => {
          try {
            const q = await api.stores.quarantaine(st.id);
            setStores((cur) =>
              cur.map((x) =>
                x.id === st.id ? { ...x, enQuarantaine: q.total ?? 0 } : x,
              ),
            );
          } catch {
            // Une quarantaine illisible ne doit pas casser la page : on
            // laisse le compte a null, et la carte n'affiche rien.
          }
        }),
      );
    } catch (error) {
      console.error("Error loading stores:", error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Produits</h1>
          <p className="text-gray-600 mt-1">
            Sélectionnez un store pour voir et gérer ses produits
          </p>
        </div>
        <button
          onClick={loadStores}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Store Cards Grid */}
      {stores.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
          <StoreIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Aucun store</h3>
          <p className="text-gray-500 mt-1">
            Créez un store pour commencer à gérer les produits
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map((store) => (
            <div
              key={store.id}
              onClick={() => navigate(`/admin/products/${store.id}`)}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md hover:border-primary/30 transition-all cursor-pointer group"
            >
              {/* Store Icon & Name */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <StoreIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                      {store.name}
                    </h3>
                    {store.glovoStoreId && (
                      <p className="text-xs text-gray-500">
                        ID Glovo: {store.glovoStoreId}
                      </p>
                    )}
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>

              {/* Address */}
              {store.address && (
                <p className="text-sm text-gray-500 mt-3 line-clamp-1">
                  📍 {store.address}
                </p>
              )}

              {/* Stats */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">
                      {store.productCount}
                    </span>{" "}
                    produit{store.productCount !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* La quarantaine : des articles que Glovo ne rend plus.
                    Ils ne sont PAS en attente de suppression — plus rien ne
                    supprime dans ce logiciel. Ils attendent que Glovo les
                    rende, et la synchronisation les en sort toute seule. */}
                {store.enQuarantaine !== null && store.enQuarantaine > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-amber-800 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      <span>
                        <span className="font-bold">{store.enQuarantaine}</span>{" "}
                        produit{store.enQuarantaine !== 1 ? "s" : ""} en
                        quarantaine
                      </span>
                    </p>
                    <p className="mt-1 text-[11px] text-amber-700/80">
                      Ils restent en rayon sur le site et reviennent
                      d&apos;eux-mêmes dès que Glovo les rend. Rien
                      n&apos;est supprimé.
                    </p>
                  </div>
                )}
              </div>

              {/* Status Badge */}
              {!store.glovoStoreId && (
                <div className="mt-3">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    ⚠️ Glovo non configuré
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}


      {/* ==================================================================
          LES EMBALLAGES CONFIES AUX FOURNISSEURS

              reste = stock initial + remis − consomme

          Seul « remis » se saisit. Le stock initial est ce qui restait a la
          fin de la quinzaine precedente, et le consomme se deduit des
          commandes livrees : chaque article porte son emballage et le nombre
          d'unites qu'il consomme.

          Demander de recompter a la main ce que la base sait deja donnerait
          deux chiffres qui ne s'accorderaient jamais, et on ne saurait pas
          lequel croire.
          ================================================================== */}
      <div className="mt-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Emballages fournisseurs
            </h2>
            {emb?.periode && (
              <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-1">
                <Calendar className="w-3.5 h-3.5" />
                Période du{" "}
                {new Date(emb.periode.debut).toLocaleDateString("fr-FR")} au{" "}
                {new Date(emb.periode.fin).toLocaleDateString("fr-FR")}
              </p>
            )}
          </div>
          {/* LE BOUTON « REMETTRE DES EMBALLAGES » A ETE RETIRE.
              Une remise ne se saisit plus seule : elle vient d'un bon de
              livraison, avec son numéro de facture et sa date. Garder les
              deux portes d'entrée, c'était garantir que la moitié des
              remises n'aurait jamais de facture en face. */}
        </div>

        {embErreur && (
          <p className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {embErreur}
          </p>
        )}

        {/* Le filtre magasin. Avec une quinzaine de fournisseurs sur deux
            magasins, tout afficher ensemble oblige a lire le petit nom du
            magasin sous chaque carte pour savoir si la ligne nous concerne.
            Le compteur sur chaque bouton evite d'ouvrir un magasin vide. */}
        {!embChargement && (emb?.fournisseurs || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { id: "", nom: "Tous les magasins" },
              ...stores.map((s: any) => ({ id: s.id, nom: s.name })),
            ].map((m) => {
              const combien =
                m.id === ""
                  ? (emb?.fournisseurs || []).length
                  : (emb?.fournisseurs || []).filter(
                      (f: any) => f.storeId === m.id,
                    ).length;
              const actif = filtreMagasin === m.id;
              return (
                <button
                  key={m.id || "tous"}
                  onClick={() => setFiltreMagasin(m.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    actif
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {m.nom}
                  <span
                    className={`ml-1.5 tabular-nums font-black ${
                      actif ? "text-white/80" : "text-gray-400"
                    }`}
                  >
                    {combien}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {embChargement ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : !emb ? (
          <button
            type="button"
            onClick={() => void chargerEmballages()}
            className="px-4 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm"
          >
            Afficher l'état des emballages
          </button>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {(emb?.fournisseurs || [])
              .filter((f: any) => !filtreMagasin || f.storeId === filtreMagasin)
              .map((f: any) => (
              <div
                key={f.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <p className="font-bold text-gray-900">{f.nom}</p>
                  {/* Le magasin sous le nom : deux fournisseurs peuvent
                      porter le meme prenom, et un stock attribue au mauvais
                      ne se rattrape pas. */}
                  {f.magasin && (
                    <p className="text-xs text-gray-500">{f.magasin}</p>
                  )}
                </div>
                {f.lignes.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-gray-400 italic">
                    Aucun emballage remis
                  </p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {f.lignes.map((l: any) => (
                      <div key={l.emballage} className="px-4 py-3">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-semibold text-gray-800">
                            {l.emballage}
                          </span>
                          {/* UN STOCK NE DESCEND PAS SOUS ZERO.
                              Un reste negatif ne veut pas dire que le
                              fournisseur doit des sacs : il veut dire qu'on
                              ne lui en a pas encore remis assez. On affiche
                              donc zero, et on dit combien il en manque. */}
                          <span
                            className={`text-xl font-black tabular-nums ${
                              l.reste <= 0 ? "text-gray-400" : "text-green-700"
                            }`}
                          >
                            {Math.max(0, l.reste)}
                          </span>
                        </div>
                        {/* Le detail du calcul est affiche : un stock qu'on
                            ne peut pas verifier n'est pas cru longtemps. */}
                        <p className="text-[11px] text-gray-500 tabular-nums mt-0.5">
                          initial {l.initial} · remis {l.remis} · consommé{" "}
                          {l.consomme}
                        </p>
                        {l.reste < 0 && (
                          <p className="text-[11px] font-semibold text-amber-700 mt-0.5">
                            {Math.abs(l.reste)} à remettre — emballages
                            consommés sans remise saisie
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* L'historique des remises de la periode. Sans lui, un stock faux
            serait impossible a expliquer : on verrait le resultat, jamais
            d'ou il vient. */}
        {/* LE PAPIER AVANT L'HISTORIQUE.
            La facture du fabricant n'entrait nulle part : on savait ce
            qu'on avait donné, jamais ce qu'on avait payé ni ce qu'il
            restait en main. */}
        {!embChargement && (emb?.fournisseurs || []).length > 0 && (
          <div className="mt-5">
            <BonLivraisonEmballages
              magasins={stores.map((st: any) => ({
                id: st.id,
                name: st.name || st.id,
              }))}
              fournisseurs={(emb.fournisseurs || []).map((f: any) => ({
                id: f.id,
                nom: f.nom || f.id,
                storeId: f.storeId || null,
              }))}
              emballagesConnus={Array.from(
                new Set(
                  (emb.fournisseurs || [])
                    .flatMap((f: any) => f.lignes || [])
                    .map((l: any) => String(l.emballage || ""))
                    .filter(Boolean),
                ),
              ) as string[]}
              onEnregistre={() => {
                void chargerEmballages(true);
                void chargerBons();
              }}
            />
          </div>
        )}

        {/* LES BONS DE LIVRAISON SAISIS — tous, pas seulement la periode,
            filtres par le magasin choisi en haut. */}
        {(() => {
          const visibles = bons.filter(
            (b: any) => !filtreMagasin || b.storeId === filtreMagasin,
          );
          if (visibles.length === 0) return null;
          const nomMag = (id: string | null) =>
            (stores.find((st: any) => st.id === id) as any)?.name || id || "";
          const dhs = (c: number) =>
            (Number(c || 0) / 100).toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          const total = visibles.reduce(
            (t: number, b: any) => t + Number(b.totalCentimes || 0),
            0,
          );
          return (
            <div className="mt-5 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-700">
                  Bons de livraison saisis ({visibles.length})
                </p>
                <p className="text-sm font-black text-gray-900 tabular-nums">
                  {dhs(total)} DH
                </p>
              </div>
              <div className="divide-y divide-gray-100">
                {visibles.map((b: any) => (
                  <div key={b.id} className="px-4 py-3">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-mono font-bold text-gray-900">
                        {b.numeroFacture || "—"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {b.le ? new Date(b.le).toLocaleDateString("fr-FR") : ""}
                      </span>
                      <span className="text-xs font-semibold text-gray-700">
                        {nomMag(b.storeId)}
                      </span>
                      {b.fabricant && (
                        <span className="text-xs text-gray-500">{b.fabricant}</span>
                      )}
                      <span className="ml-auto text-sm font-black text-gray-900 tabular-nums">
                        {b.totalCentimes != null ? `${dhs(b.totalCentimes)} DH` : ""}
                      </span>
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {(b.lignes || []).map((l: any, i: number) => (
                        <li key={i} className="text-sm text-gray-700">
                          {l.quantite} × {l.emballage}
                          {l.resteMagasin > 0 && (
                            <span className="text-gray-500">
                              {" "}— {l.resteMagasin} au responsable commande
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {b.note && (
                      <p className="mt-1 text-xs italic text-gray-500">{b.note}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {!embChargement &&
          (emb?.remises || []).some((r: any) => {
            if (!filtreMagasin) return true;
            const f = (emb.fournisseurs || []).find(
              (x: any) => x.id === r.fournisseurId,
            );
            return f?.storeId === filtreMagasin;
          }) && (
          <div className="mt-5 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <p className="px-4 py-3 border-b border-gray-100 bg-gray-50 text-sm font-semibold text-gray-700">
              Remises de la période
            </p>
            <div className="divide-y divide-gray-100">
              {emb.remises
                .filter((r: any) => {
                  // L'historique suit le meme filtre que les cartes : voir
                  // un total pour un magasin et un historique pour tous les
                  // autres, c'est la garantie de ne plus savoir lequel des
                  // deux ment.
                  if (!filtreMagasin) return true;
                  const f = (emb.fournisseurs || []).find(
                    (x: any) => x.id === r.fournisseurId,
                  );
                  return f?.storeId === filtreMagasin;
                })
                .map((r: any) => {
                const f = (emb.fournisseurs || []).find(
                  (x: any) => x.id === r.fournisseurId,
                );
                return (
                  <div
                    key={r.id}
                    className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="text-gray-500 tabular-nums shrink-0">
                      {new Date(r.le).toLocaleDateString("fr-FR")}
                    </span>
                    <span className="font-semibold text-gray-800 flex-1 truncate">
                      {f?.nom || r.fournisseurId}
                    </span>
                    <span className="text-gray-600 truncate">{r.emballage}</span>
                    <span className="font-black tabular-nums text-gray-900 shrink-0">
                      +{r.quantite}
                    </span>
                    {r.coutTotalCentimes !== null &&
                      r.coutTotalCentimes !== undefined && (
                        <span className="text-xs text-gray-500 tabular-nums shrink-0">
                          {(r.coutTotalCentimes / 100).toLocaleString("fr-FR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          DH
                        </span>
                      )}
                    {/* ANNULER UNE SAISIE FAUSSE.
                        Se tromper d'emballage ou de fournisseur arrive en
                        remettant les sacs debout. Sans ce bouton, l'erreur
                        restait dans le stock pour toujours. On supprime la
                        ligne, puis on ressaisit la bonne. */}
                    <button
                      type="button"
                      onClick={() => {
                        setRemiseASupprimer(r);
                        setEditEmballage(r.emballage || TYPES_EMBALLAGE[0]);
                        setEditQte(String(r.quantite ?? ""));
                        setEditPrix(
                          r.prixUnitaireCentimes === null ||
                            r.prixUnitaireCentimes === undefined
                            ? ""
                            : String(r.prixUnitaireCentimes / 100),
                        );
                      }}
                      title="Modifier cette remise"
                      className="shrink-0 text-xs font-semibold text-gray-400 hover:text-primary px-2"
                    >
                      modifier
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* MODIFIER UNE REMISE DEJA SAISIE.
          Se tromper d'emballage arrive en remettant les sacs debout. On
          corrige la ligne plutot que de l'effacer : la remise a bien eu
          lieu, c'est ce qu'on en avait note qui etait faux. */}
      {remiseASupprimer && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setRemiseASupprimer(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Modifier la remise
              </h3>
              <p className="text-sm text-gray-500">
                {(emb?.fournisseurs || []).find(
                  (x: any) => x.id === remiseASupprimer.fournisseurId,
                )?.nom || remiseASupprimer.fournisseurId}{" "}
                · {new Date(remiseASupprimer.le).toLocaleDateString("fr-FR")}
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                Emballage
              </label>
              <select
                value={editEmballage}
                onChange={(e) => setEditEmballage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              >
                {TYPES_EMBALLAGE.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Quantité
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={editQte}
                  onChange={(e) => setEditQte(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Prix unitaire (DH)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editPrix}
                  onChange={(e) => setEditPrix(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setRemiseASupprimer(null)}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 font-semibold"
              >
                Fermer
              </button>
              <button
                type="button"
                disabled={suppressionEnCours}
                onClick={async () => {
                  try {
                    setSuppressionEnCours(true);
                    const q = parseInt(editQte, 10);
                    const p = parseFloat(editPrix.replace(",", "."));
                    await api.emballages.modifierRemise(remiseASupprimer.id, {
                      emballage: editEmballage,
                      quantite: Number.isFinite(q) && q > 0 ? q : undefined,
                      prixUnitaire: Number.isFinite(p) ? p : undefined,
                    });
                    setRemiseASupprimer(null);
                    setEmb(await api.emballages.etat(undefined, undefined, true));
                  } catch (e) {
                    console.error(e);
                  } finally {
                    setSuppressionEnCours(false);
                  }
                }}
                className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold disabled:opacity-50"
              >
                {suppressionEnCours ? "…" : "Enregistrer"}
              </button>
            </div>

            {/* La suppression reste possible, mais discrete : une remise
                qui n'a jamais eu lieu ne se corrige pas, elle s'efface. */}
            <button
              type="button"
              disabled={suppressionEnCours}
              onClick={async () => {
                try {
                  setSuppressionEnCours(true);
                  await api.emballages.supprimerRemise(remiseASupprimer.id);
                  setRemiseASupprimer(null);
                  setEmb(await api.emballages.etat(undefined, undefined, true));
                } catch (e) {
                  console.error(e);
                } finally {
                  setSuppressionEnCours(false);
                }
              }}
              className="w-full text-xs text-gray-400 hover:text-red-600 font-semibold"
            >
              supprimer cette remise
            </button>
          </div>
        </div>
      )}

      {/* La saisie d'une remise. Trois champs, pas un de plus : on la fait
          debout, en remettant les sacs. */}
      {remiseOuverte && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={enregistrerRemise}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Remettre des emballages
              </h3>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Magasin
                </label>
                <select
                  value={remiseMagasin}
                  onChange={(e) => {
                    setRemiseMagasin(e.target.value);
                    // Changer de magasin doit oublier le fournisseur : le
                    // garder laisserait un nom qui n'appartient plus a la
                    // liste affichee, et on enregistrerait chez quelqu'un
                    // qu'on ne voit plus.
                    setRemiseFrs("");
                  }}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Choisir…</option>
                  {stores.map((st) => (
                    <option key={st.id} value={st.id}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Fournisseur
                </label>
                <select
                  value={remiseFrs}
                  onChange={(e) => setRemiseFrs(e.target.value)}
                  required
                  disabled={!remiseMagasin}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">
                    {remiseMagasin ? "Choisir…" : "Choisis d'abord un magasin"}
                  </option>
                  {(emb?.fournisseurs || [])
                    .filter((f: any) => f.storeId === remiseMagasin)
                    .map((f: any) => (
                      <option key={f.id} value={f.id}>
                        {f.nom}
                      </option>
                    ))}
                </select>
                {remiseMagasin &&
                  (emb?.fournisseurs || []).filter(
                    (f: any) => f.storeId === remiseMagasin,
                  ).length === 0 && (
                    <p className="mt-1 text-xs text-amber-700">
                      Aucun fournisseur rattaché à ce magasin.
                    </p>
                  )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Emballage
                </label>
                <select
                  value={remiseType}
                  onChange={(e) => setRemiseType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {TYPES_EMBALLAGE.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Quantité remise
                </label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={remiseQte}
                  onChange={(e) => setRemiseQte(e.target.value)}
                  required
                  placeholder="1000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-lg font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Prix d&apos;achat de la pièce{" "}
                  <span className="font-normal text-gray-400">
                    (facultatif)
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={remisePrix}
                    onChange={(e) => setRemisePrix(e.target.value)}
                    placeholder="0,90"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-12 text-lg font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                    DH
                  </span>
                </div>
                {/* Le total s'affiche pendant la frappe : c'est le seul
                    moyen d'attraper une virgule mal placee avant
                    d'enregistrer. 0,90 et 90 ne se voient pas pareil une
                    fois multiplies par mille. */}
                {(() => {
                  const p = parseFloat(remisePrix.replace(",", "."));
                  const q = parseInt(remiseQte, 10);
                  if (!Number.isFinite(p) || !Number.isFinite(q) || q <= 0) {
                    return null;
                  }
                  return (
                    <p className="mt-1 text-xs text-gray-600 tabular-nums">
                      Coût total :{" "}
                      <span className="font-bold text-gray-900">
                        {(p * q).toLocaleString("fr-FR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        DH
                      </span>
                    </p>
                  );
                })()}
              </div>
            </div>

            <div className="px-5 pb-5 flex gap-2">
              <button
                type="button"
                onClick={() => setRemiseOuverte(false)}
                disabled={remiseEnCours}
                className="px-5 py-2.5 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={
                  remiseEnCours || !remiseMagasin || !remiseFrs || !remiseQte
                }
                className="flex-1 px-5 py-2.5 rounded-lg bg-primary text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {remiseEnCours ? "..." : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
        <div className="flex gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <h4 className="font-medium text-blue-900">Comment ça marche ?</h4>
            <p className="text-sm text-blue-700 mt-1">
              Chaque store a son propre catalogue avec des prix et
              disponibilités indépendants. Cliquez sur un store pour voir ses
              produits ou les synchroniser depuis Glovo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
